import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /** ✅ Save data (JSON.stringify if not a string) */
  set<T>(key: string, value: T): void {
    if (!this.isBrowser) return;

    try {
      const serialized =
        typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error('Error saving to localStorage', error);
    }
  }

  /** ✅ Get data (auto JSON.parse if possible) */
  get<T>(key: string): T | null {
    if (!this.isBrowser) return null;

    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;

      try {
        return JSON.parse(item) as T; // If it's JSON
      } catch {
        return item as unknown as T; // If plain string
      }
    } catch (error) {
      console.error('Error reading from localStorage', error);
      return null;
    }
  }

  /** ✅ Remove a single key */
  remove(key: string): void {
    if (!this.isBrowser) return;

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage', error);
    }
  }

  /** ✅ Remove multiple keys at once */
  removeMultiple(keys: string[]): void {
    if (!this.isBrowser) return;

    keys.forEach((key) => this.remove(key));
  }

  /** ✅ Clear entire localStorage */
  clear(): void {
    if (!this.isBrowser) return;

    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage', error);
    }
  }

  /** ✅ Check if key exists */
  exists(key: string): boolean {
    if (!this.isBrowser) return false;
    return localStorage.getItem(key) !== null;
  }
}
